import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { classes, stylesheet } from 'typestyle';
import {
  Alert,
  Black,
  ButtonIcon,
  Checkbox,
  Gray100,
  Gray170,
  Gray20,
  TextInput,
  Tooltip,
  typeCompact12RegularProperties,
  typeCompact14MediumProperties,
  typeCompact14RegularProperties,
} from '@traceair/tds';
import { EditIcon, InfoOutlineIcon, RefreshIcon } from '@traceair/webapp-icons';
import { tooltipOverlayClass } from '../../../../../components/layout/styles';
import { numericOnly } from '../../../../../libs/numericOnly';
import { GcpRequestData } from '../../../../../store/uploadStore';

const css = stylesheet({
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderTop: `1px solid ${Gray20}`,
  },
  rowReadonly: {
    paddingRight: 24,
  },
  left: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
  label: {
    ...typeCompact14RegularProperties,
    color: Black,
  },
  count: {
    ...typeCompact12RegularProperties,
    color: Gray100,
  },
  infoIconContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 40,
  },
  hoursValue: {
    ...typeCompact14MediumProperties,
    color: Gray170,
  },
  inputWrap: {
    width: 140,
  },
  tooltipTrigger: {
    margin: '-4px 0',
  },
  confirmation: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: -4,
    padding: '0 16px 12px',
  },
  confirmationCheckbox: {
    alignSelf: 'flex-start',
  },
});

const hoursToStr = (hours: number | undefined): string => {
  if (hours === undefined) return '';
  return hours.toString();
};

const parseHoursInput = (input: string): number | undefined => {
  const parsed = parseFloat(input);
  if (isNaN(parsed) || parsed < 0) return undefined;
  return Math.ceil(parsed / 0.25) * 0.25; // round up to nearest 0.25 h (15 min)
};

const HoursSuffix = () => (
  <span style={{ color: Gray100, display: 'flex', alignItems: 'center', paddingTop: 3 }}>hours</span>
);

const formatGcpDate = (date: string) => format(parseISO(date), 'MMM d, yyyy');

export const shouldConfirmGcpServiceHours = (req: GcpRequestData) =>
  req.meta.isEditMode &&
  (req.measurements.toString() !== req.meta.prevMeasurements.toString() ||
    req.gcpServiceLaborTimeInHours !== req.meta.prevGcpServiceLaborTimeInHours);

interface ConfirmationProps {
  measurementDates: string[];
  confirmed: boolean;
  onConfirmedChange: (confirmed: boolean) => void;
}

export const GcpServiceHoursConfirmation: React.FC<ConfirmationProps> = ({
  measurementDates,
  confirmed,
  onConfirmedChange,
}) => (
  <div className={css.confirmation}>
    {measurementDates.length > 1 && (
      <Alert
        type="attention"
        description={`Your measurements are from ${formatGcpDate(measurementDates[0])} to ${formatGcpDate(
          measurementDates[measurementDates.length - 1],
        )}, so the hours cover all these days. Change the hours to the time you spent on this mission.`}
      />
    )}
    <Checkbox
      className={css.confirmationCheckbox}
      label="Confirm service hours"
      checked={confirmed}
      onChange={onConfirmedChange}
    />
  </div>
);

interface Props {
  hours: number | undefined;
  defaultHours?: number;
  gcpCount?: number;
  serviceDate?: string;
  isEditMode: boolean;
  onChange: (hours: number | undefined) => void;
}

export const GcpServiceHoursRow: React.FC<Props> = ({
  hours,
  defaultHours,
  gcpCount,
  serviceDate,
  isEditMode,
  onChange,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [hoursInput, setHoursInput] = useState('');

  const handleEditClick = () => {
    setHoursInput(hoursToStr(hours));
    setIsEditing(true);
  };

  const handleBlur = () => {
    onChange(parseHoursInput(hoursInput));
    setIsEditing(false);
  };

  const handleReset = () => {
    onChange(defaultHours);
    setIsEditing(false);
  };

  const displayHours = hoursToStr(hours);
  const details = [
    gcpCount !== undefined && `${gcpCount} GCPs measured`,
    serviceDate && `Service date ${formatGcpDate(serviceDate)}`,
  ]
    .filter(Boolean)
    .join(' · ');
  const canReset =
    defaultHours !== undefined && (isEditing ? parseHoursInput(hoursInput) !== defaultHours : hours !== defaultHours);

  return (
    <div className={classes(css.row, !isEditing && !isEditMode && css.rowReadonly)}>
      <div className={css.left}>
        <div className={css.labelRow}>
          <span className={css.label}>GCP service hours</span>
          <Tooltip
            arrow
            description="Estimated from the interval between the first and last GCP points, plus 30 min for preparation."
            offset={4}
            placement="top"
            styleType="neutral-high"
            trigger="hover"
            overlayClassName={tooltipOverlayClass}
          >
            <div className={classes(css.tooltipTrigger, css.infoIconContainer)}>
              <InfoOutlineIcon color={Gray100} size={12} />
            </div>
          </Tooltip>
        </div>
        {details && <span className={css.count}>{details}</span>}
      </div>

      <div className={css.right}>
        {isEditing ? (
          <>
            <div className={css.inputWrap}>
              <TextInput
                autoFocus
                value={hoursInput}
                placeholder="0"
                rightCell={<HoursSuffix />}
                onChange={(value) => setHoursInput(numericOnly(value))}
                onBlur={handleBlur}
              />
            </div>
            {canReset && (
              <ButtonIcon
                style="tertiary"
                type="black"
                size="xs"
                icon={<RefreshIcon size={16} />}
                onClick={handleReset}
              />
            )}
          </>
        ) : (
          <>
            <span className={css.hoursValue}>{displayHours ? `${displayHours} hours` : '—'}</span>
            {canReset && isEditMode && (
              <ButtonIcon
                style="tertiary"
                type="black"
                size="xs"
                icon={<RefreshIcon size={16} color={Gray170} />}
                onClick={handleReset}
              />
            )}
            {isEditMode && (
              <ButtonIcon
                style="tertiary"
                type="black"
                size="xs"
                icon={<EditIcon size={16} color={Gray170} />}
                onClick={handleEditClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
